import { XMarkIcon } from "@heroicons/react/24/outline";
import { animated, useSpring } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import { useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "ui/core/button";

import { useLang } from "./use-lang";

export const useBottomPanel = () => {
  const { t } = useLang();
  const [isOpen, setIsOpen] = useState(false);

  const [panelStyle, api] = useSpring(() => ({
    top: 100,
    y: 0,
  }));

  const openPanel = () => {
    api.start({ top: 0, y: 0 });
    setIsOpen(true);
  };

  const closePanel = () => {
    api.start({ top: 100, y: 0 });
    setIsOpen(false);
  };

  const bind = useDrag(
    ({ event, last, offset: [, oy] }) => {
      event.preventDefault();
      api.start({ y: oy });
      if (last && oy > 100) {
        closePanel();
      } else if (last) {
        api.start({ y: 0 });
      }
    },
    {
      from: () => [0, panelStyle.y.get()],
      axis: "y",
      bounds: { top: 0 },
    }
  );

  const Panel = ({
    children,
    title,
  }: {
    children: React.ReactNode;
    title?: React.ReactNode;
  }) =>
    typeof window === "undefined"
      ? null
      : createPortal(
          <>
            {isOpen && (
              <div
                className="fixed inset-0 z-[99999] bg-black bg-opacity-50"
                onClick={closePanel}
              />
            )}
            <animated.div
              className="fixed inset-0 z-[99999] flex transform touch-none flex-col rounded-t-2xl bg-white"
              style={{
                top: panelStyle.top.to((v) => `${v}%`),
                y: panelStyle.y,
              }}
            >
              <div
                {...bind()}
                className="flex flex-col gap-4 border-b border-[#EBEBEB] px-6 pb-4 pt-2"
              >
                <div className="mx-auto h-1.5 w-12 rounded-full bg-[#EBEBEB]" />
                <div className="flex items-center gap-3">
                  {title && (
                    <div className="flex flex-1 items-center">
                      {typeof title === "string" ? (
                        <h3 className="text-center text-lg font-bold">
                          {title}
                        </h3>
                      ) : (
                        title
                      )}
                    </div>
                  )}
                  <div
                    className="flex h-8 w-8 items-center justify-center"
                    onClick={closePanel}
                  >
                    <XMarkIcon
                      width={24}
                      height={24}
                      className="text-zinc-500"
                      aria-label="close chart panel"
                    />
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-3 py-2">{children}</div>
              <div className="px-3 py-4">
                <Button onClick={closePanel} className="w-full">
                  {t.winwingpt.close}
                </Button>
              </div>
            </animated.div>
          </>,
          document?.body
        );

  return {
    openPanel,
    closePanel,
    Panel,
  };
};
